import { useCallback } from 'react'
import { useLocation } from 'wouter'
import { useLingui } from '@lingui/react/macro'
import { GraphQL } from '@guestgoodie/graphql'

import useCategories from '../hooks/useCategories'
import CategoryItem from './CategoryItem'
import CategoryDetail from './CategoryDetail'
import { EmptyView } from '@/components/ui/EmptyView'
import Loading from '@/components/ui/Loading'

interface Props {
  layout: GraphQL.LayoutFragmentFragment
  categorySlug?: string
  readOnly: boolean
}

export default function LayoutDetail({
  layout,
  categorySlug,
  readOnly
}: Props) {
  const { t } = useLingui()
  const [, navigate] = useLocation()

  const { categories, loading, editCategory, removeCategory, moveCategory } =
    useCategories(layout)

  const onSelectCategory = useCallback(
    (slug: string) => {
      navigate(`/layout/${layout.version}/${slug}`)
    },
    [layout.version, navigate]
  )

  return (
    <main className="flex flex-col md:flex-row gap-2.5">
      <section className="flex flex-col gap-2.5 px-2.5">
        {loading && <Loading />}
        {!loading && categories.length === 0 && (
          <div className="max-w-[500px] shrink-0 grow-1 rounded bg-white p-10">
            <EmptyView text={t`No categories yet`} />
          </div>
        )}
        {categories.length > 0 && (
          <div className="max-w-[500px] shrink-0 grow-1 columns-2 gap-4 rounded bg-white p-5">
            {categories.map(function (category) {
              return (
                <div
                  // The translation 0 is needed since there is a chrome bug that f`s up the drag preview without it
                  // see: https://github.com/react-dnd/react-dnd/issues/832#issuecomment-442071628
                  className="translate-x-0 translate-y-0 break-inside-avoid-column"
                  key={category.slug}
                >
                  <CategoryItem
                    category={category}
                    isActive={categorySlug === category.slug}
                    onClick={() => onSelectCategory(category.slug)}
                    onChangePosition={position =>
                      moveCategory(category.slug, position)
                    }
                    onEdit={editCategory}
                    onRemove={removeCategory}
                    readOnly={readOnly}
                  />
                </div>
              )
            })}
          </div>
        )}
      </section>
      {categorySlug && (
        <CategoryDetail
          layoutId={layout.id}
          slug={categorySlug}
          readOnly={readOnly}
        />
      )}
    </main>
  )
}
